/**
 * MergeConfirmationDialog — R3, SPEC-invoice-merge-2026-08-17.
 *
 * There is no undo for a merge (spec.yaml cross-cutting constraint), so this
 * confirmation carries the whole weight of the decision: before anything
 * happens, it names which invoice number survives, which is retired, how
 * many line items will move, and the resulting total (AC4). It is invoked
 * only when exactly two invoices are selected (AC1/AC3, enforced by the
 * caller in InvoiceListPage).
 *
 * Deliberately a pure, data-only component with no react-query / api
 * dependency, mirroring SendConfirmationDialog.tsx
 * (SPEC-invoice-send-safety-2026-08-17 R1) -- the caller computes the
 * preview values (survivor/absorbed numbers, line count, resulting total via
 * lib/invoice-merge.ts) and passes them in as props. This keeps the
 * confirmation's own component tests focused on "given this data, does the
 * dialog render it correctly" (AC5) without needing to mock the merge
 * mutation or invoice-item fetches.
 *
 * Shape follows the same AlertDialog idiom SendConfirmationDialog already
 * established for this release's other new confirmation (title names the
 * action + subject, description lists the specific data as a labelled list,
 * footer is Cancel + a single primary action whose label names the outcome)
 * -- see output.md's AC10 section for how this was checked against the
 * sibling spec.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface MergeConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Invoice number that will keep its own number after the merge. */
  survivorInvoiceNumber: string;
  /** Invoice number that will be retired (cancelled, pointed at the survivor). */
  absorbedInvoiceNumber: string;
  /** How many of the retired invoice's line items will move onto the survivor. */
  movedLineItemCount: number | null;
  /** Recomputed resulting total for the surviving invoice, or null while still loading. */
  resultingTotal: number | null;
  /** Client the pair bills, shown so the wrong pair is recognisable as wrong. */
  clientName?: string | null;
  /** Project the pair bills. A merge is only legal within one project. */
  projectName?: string | null;
  /**
   * The PO number the COMBINED invoice will print, after the survivor inherits
   * the absorbed invoice's PO when it has none of its own.
   *
   * Shown because a PO can also live in the client-facing notes -- that is
   * where they lived before the column existed, and she was told to put them
   * there. When one invoice carries a real po_number and the other carries a
   * PO in its notes, the conflict refusal cannot see it and BOTH end up on the
   * combined bill. This line and the notes preview below are the only place
   * she can catch that, because a merge does not pass through the PDF preview
   * she performs on every invoice before sending.
   */
  resultingPoNumber?: string | null;
  /** The client-facing notes the combined invoice will print. These RENDER ON
   *  THE PDF (internal notes do not), and the merge appends the absorbed
   *  invoice's onto the survivor's. */
  resultingExternalNotes?: string | null;
  /** True while the merge mutation is in flight — disables both actions. */
  pending?: boolean;
  /**
   * Set when the last confirm attempt was refused or failed. Shown inline
   * rather than closing the dialog, so both invoices stay visibly unchanged
   * and the user can see exactly why (AC7, AC9) without losing the pair
   * they were trying to merge.
   */
  error?: { code?: string; message: string } | null;
  /**
   * Set BEFORE confirmation when the pair can already be seen to be
   * ineligible (different project, different client). Disables the confirm
   * action. Added 2026-09-13: the v0.17 preview offered an enabled "Merge
   * Invoices" for a cross-project pair, under "There is no undo", and the
   * refusal only arrived as a 409 after she had confirmed. The backend's
   * DIFFERENT_PROJECT / DIFFERENT_CLIENT refusals remain the authority; this
   * only stops the dialog inviting a click it knows will be refused.
   */
  blockedReason?: string | null;
  /**
   * Cancel-invoice feature. Christa-lens redirect (2026-09-15): offered ONLY
   * for the DIFFERENT_PROJECT refusal -- its message is the only one that
   * says "...recreate it on the right project, then cancel the wrong one."
   * On a DIFFERENT_CLIENT refusal both invoices can be entirely legitimate
   * (billed to different clients is not a mistake on either one), and that
   * refusal never mentions cancelling -- so the caller passes an EMPTY
   * array for that case; this component doesn't know which refusal is
   * active, it only renders whatever pair it's handed. Each entry renders
   * its own button only when `cancellable` is true (not already cancelled,
   * no active payment) -- an invoice that's already unmergeable for a
   * DIFFERENT reason (e.g. it's paid) isn't cancellable either, so no
   * button is offered for it.
   */
  blockedInvoices?: Array<{ id: string; invoiceNumber: string; cancellable: boolean }>;
  /** Fired when a "Cancel invoice #N" button is clicked. The caller closes
   *  this dialog and opens CancelInvoiceDialog for that invoice id. */
  onCancelInvoice?: (invoiceId: string) => void;
  onConfirm: () => void;
}

export function MergeConfirmationDialog({
  open,
  onOpenChange,
  survivorInvoiceNumber,
  absorbedInvoiceNumber,
  movedLineItemCount,
  resultingTotal,
  clientName = null,
  projectName = null,
  resultingPoNumber = null,
  resultingExternalNotes = null,
  pending = false,
  error = null,
  blockedReason = null,
  blockedInvoices = [],
  onCancelInvoice,
  onConfirm,
}: MergeConfirmationDialogProps) {
  const previewReady = movedLineItemCount !== null && resultingTotal !== null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="merge-confirmation-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Combine invoice {absorbedInvoiceNumber} into invoice {survivorInvoiceNumber}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div data-testid="merge-confirmation-details">
              <p>This combines two invoices into one. There is no undo.</p>
              {/* v0.17 R6 MED-2: the reassurance Jason gave her by email on
                  2026-08-28 ("merging doesn't touch project allocation. Each
                  line keeps its own project tag, so Jamie's scope is
                  unaffected"), which existed nowhere in the product. Her fear
                  is stated twice that same day: "I'm petrified that if I
                  delete or move or change something taht it will mess with
                  the scope of Jamie's project outline" and "How do I change
                  this without messing up either project scope."

                  It is TRUE as written, not a comfort line: the merge's only
                  write to the lines is
                  `UPDATE invoice_items SET invoice_id = ...`
                  (services/invoice_merge_execution.py), invoice_items carries
                  no project column of its own, and each line's project tag
                  lives on its billable_item, which the merge never touches.
                  A merge is same-project-only to begin with
                  (invoice_merge_eligibility's DIFFERENT_PROJECT refusal), and
                  the executor re-asserts afterwards that no surviving line's
                  billable item belongs to another project. */}
              <p className="mt-1" data-testid="merge-confirm-project-scope">
                Each line keeps its own project tag, so project scope and
                allocations are unaffected.
              </p>
              <ul className="mt-2 space-y-1">
                <li data-testid="merge-confirm-survivor">
                  <span className="font-medium">Keeps invoice number:</span> {survivorInvoiceNumber}
                </li>
                <li data-testid="merge-confirm-retired">
                  <span className="font-medium">Retires invoice number:</span> {absorbedInvoiceNumber}
                </li>
                <li data-testid="merge-confirm-line-count">
                  <span className="font-medium">Line items moving:</span>{' '}
                  {movedLineItemCount === null ? 'Calculating…' : movedLineItemCount}
                </li>
                {clientName && (
                  <li data-testid="merge-confirm-client">
                    <span className="font-medium">Client:</span> {clientName}
                  </li>
                )}
                {projectName && (
                  <li data-testid="merge-confirm-project">
                    <span className="font-medium">Project:</span> {projectName}
                  </li>
                )}
                <li data-testid="merge-confirm-total">
                  <span className="font-medium">New total:</span>{' '}
                  {resultingTotal === null
                    ? 'Calculating…'
                    : `$${resultingTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </li>
              </ul>
              {/* What the CLIENT will read. The merge rewrites both of these,
                  and neither was visible here before -- on the one operation
                  in this release that cannot be undone. */}
              {(resultingPoNumber || resultingExternalNotes) && (
                <div className="mt-3 rounded border p-2" data-testid="merge-confirm-prints">
                  <p className="font-medium">The combined invoice will print:</p>
                  {resultingPoNumber && (
                    <p data-testid="merge-confirm-po">
                      <span className="font-medium">PO number:</span> {resultingPoNumber}
                    </p>
                  )}
                  {resultingExternalNotes && (
                    <p data-testid="merge-confirm-notes" className="whitespace-pre-line">
                      <span className="font-medium">Notes to the client:</span>
                      <br />
                      {resultingExternalNotes}
                    </p>
                  )}
                </div>
              )}
              {blockedReason && (
                <>
                  <p role="alert" data-testid="merge-confirmation-blocked" className="mt-3 text-destructive">
                    {blockedReason}
                  </p>
                  {/* Cancel-invoice feature: on the DIFFERENT_PROJECT refusal
                      the text above tells her to "recreate it on the right
                      project, then cancel the wrong one" -- these buttons ARE
                      that "cancel the wrong one" step. The caller only ever
                      passes a non-empty blockedInvoices for THAT refusal --
                      never for DIFFERENT_CLIENT, where both invoices can be
                      legitimate and the message never mentions cancelling. */}
                  {blockedInvoices.filter((inv) => inv.cancellable).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="merge-confirmation-cancel-offers">
                      {blockedInvoices.filter((inv) => inv.cancellable).map((inv) => (
                        <button
                          key={inv.id}
                          type="button"
                          data-testid={`merge-confirmation-cancel-${inv.id}`}
                          className="rounded border border-destructive px-3 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/10"
                          onClick={() => onCancelInvoice?.(inv.id)}
                        >
                          Cancel invoice #{inv.invoiceNumber}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              {error && (
                <p role="alert" data-testid="merge-confirmation-error" className="mt-3 text-destructive">
                  {error.message}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="merge-confirmation-cancel"
            aria-label={`Cancel merging invoice ${absorbedInvoiceNumber} into invoice ${survivorInvoiceNumber}`}
            disabled={pending}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="merge-confirmation-confirm"
            aria-label={`Confirm merge of invoice ${absorbedInvoiceNumber} into invoice ${survivorInvoiceNumber}`}
            disabled={pending || !previewReady || !!blockedReason}
            onClick={(e) => {
              // AlertDialogAction closes on click by default (Radix); we drive
              // closing ourselves via onConfirm/onOpenChange so the caller can
              // guard against a double-dispatch and keep the dialog open on
              // refusal (AC6, AC7, AC9).
              e.preventDefault();
              onConfirm();
            }}
          >
            {pending ? 'Merging…' : 'Merge Invoices'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
